// SimpleTradesHistory/SimpleTradeTable.jsx
import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import {
  COLORS,
  formatPrice,
  formatPnL,
  formatPercent,
  formatDuration,
} from './constants';

// ==================== TRADE ROW ====================
const TradeRow = ({ trade, index, direction, trackedSignals }) => {
  const pnl = parseFloat(trade.PnL) || 0;
  const pnlPct = parseFloat(trade.PnL_Pct) || 0;
  const isWin = pnl > 0;
  const pnlColor = isWin ? COLORS.success : COLORS.danger;

  const cellStyle = {
    borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
    py: 1.25,
    px: 1,
  };

  return (
    <TableRow
      sx={{
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: alpha(COLORS.primary, 0.05),
        },
      }}
    >
      {/* Trade Number */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.primary,
            fontWeight: 700,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
          }}
        >
          #{index + 1}
        </Typography>
      </TableCell>

      {/* Status (WIN/LOSS) */}
      <TableCell sx={cellStyle}>
        <Chip
          icon={
            isWin ? (
              <TrendingUpIcon sx={{ fontSize: 12 }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 12 }} />
            )
          }
          label={isWin ? 'WIN' : 'LOSS'}
          size="small"
          sx={{
            bgcolor: alpha(pnlColor, 0.15),
            color: pnlColor,
            fontWeight: 700,
            fontSize: '0.65rem',
            height: 22,
            '& .MuiChip-icon': { color: pnlColor },
          }}
        />
      </TableCell>

      {/* Date */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.text.secondary,
            fontSize: '0.75rem',
          }}
        >
          {trade.Date}
        </Typography>
      </TableCell>

      {/* Tracked Signals - show full datetime */}
      {trackedSignals.map((signalKey) => (
        <TableCell key={signalKey} sx={cellStyle}>
          {trade[signalKey] ? (
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text.muted,
                fontFamily: 'monospace',
                fontSize: '0.7rem',
              }}
            >
              {trade[signalKey]}
            </Typography>
          ) : (
            <Typography sx={{ color: alpha(COLORS.text.muted, 0.3), fontSize: '0.7rem' }}>
              —
            </Typography>
          )}
        </TableCell>
      ))}

      {/* Entry Time */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.success,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            fontWeight: 600,
          }}
        >
          {trade['Entry Time']}
        </Typography>
      </TableCell>

      {/* Entry Price */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.success,
            fontWeight: 600,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          }}
        >
          {formatPrice(trade['Entry Price'])}
        </Typography>
      </TableCell>

      {/* Exit Time */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.danger,
            fontFamily: 'monospace',
            fontSize: '0.7rem',
            fontWeight: 600,
          }}
        >
          {trade['Exit Time']}
        </Typography>
      </TableCell>

      {/* Exit Price */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: COLORS.danger,
            fontWeight: 600,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          }}
        >
          {formatPrice(trade['Exit Price'])}
        </Typography>
      </TableCell>

      {/* Duration */}
      <TableCell sx={cellStyle}>
        <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontSize: '0.75rem' }}>
          {formatDuration(trade['Entry Time'], trade['Exit Time'])}
        </Typography>
      </TableCell>

      {/* P&L (pts) */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: pnlColor,
            fontWeight: 700,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
          }}
        >
          {formatPnL(pnl)}
        </Typography>
      </TableCell>

      {/* P&L % */}
      <TableCell sx={cellStyle}>
        <Typography
          variant="body2"
          sx={{
            color: pnlColor,
            fontWeight: 600,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
          }}
        >
          {formatPercent(pnlPct)}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

// ==================== MAIN TABLE COMPONENT ====================
const SimpleTradeTable = ({ trades, direction }) => {
  // Extract tracked signal keys dynamically from first trade
  const standardFields = ['Trade', 'Date', 'Entry Time', 'Exit Time', 'Entry Price', 'Exit Price', 'PnL', 'PnL_Pct'];
  
  // Get tracked signals from first trade record
  const trackedSignals = trades?.length > 0
    ? Object.keys(trades[0]).filter(key => !standardFields.includes(key))
    : [];
  
  // Color map for tracked signals
  const signalColors = {
    'Lux Long Entry': COLORS.primary,
    'Lux Short Entry': COLORS.danger,
    'Smart Band Break': COLORS.warning,
    'Smart Trail Break': COLORS.success,
    'Lux Short': COLORS.danger,
    'Lux Long': COLORS.primary,
    'Cobra Short': COLORS.purple,
    'Cobra Long': COLORS.purple,
    'Lux 12 Entry': COLORS.pink,
  };
  
  // Build dynamic headers
  const trackedSignalHeaders = trackedSignals.map(key => ({
    key: key.replace(' Entry', '').replace('Smart Band ', '').replace('Smart Trail ', 'Trail '),
    fullKey: key,
    color: signalColors[key] || COLORS.text.muted,
  }));

  const headers = [
    { key: '#', color: COLORS.text.muted },
    { key: 'Status', color: COLORS.text.muted },
    { key: 'Date', color: COLORS.text.muted },
    ...trackedSignalHeaders,
    { key: 'Entry', color: COLORS.success },
    { key: 'Entry $', color: COLORS.success },
    { key: 'Exit', color: COLORS.danger },
    { key: 'Exit $', color: COLORS.danger },
    { key: 'Duration', color: COLORS.text.muted },
    { key: 'P&L', color: COLORS.text.muted },
    { key: 'P&L %', color: COLORS.text.muted },
  ];

  // Empty state
  if (!trades?.length) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <HistoryIcon
          sx={{ fontSize: 64, color: alpha(COLORS.text.muted, 0.3), mb: 2 }}
        />
        <Typography variant="h6" sx={{ color: COLORS.text.muted, mb: 0.5 }}>
          No trades found
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(COLORS.text.muted, 0.6) }}>
          Adjust filters or date range to view trades
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: alpha(COLORS.bg.tertiary, 0.9) }}>
            {headers.map((header) => (
              <TableCell
                key={header.key}
                sx={{
                  color: header.color,
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
                  py: 1.25,
                  px: 1,
                  bgcolor: alpha(COLORS.bg.tertiary, 0.95),
                  whiteSpace: 'nowrap',
                }}
              >
                {header.key}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {trades.map((trade, index) => (
            <TradeRow
              key={`${trade.Date}-${index}`}
              trade={trade}
              index={index}
              direction={direction}
              trackedSignals={trackedSignals}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SimpleTradeTable;
